import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { translations, Translations, UILanguage } from '@/lib/i18n/translations';

interface LanguageContextType {
  language: UILanguage;
  setLanguage: (lang: UILanguage) => void;
  t: Translations;
  isLoading: boolean;
  // Helper to get the target word (Turkish translation)
  getTargetWord: (item: {
    target_word_tr?: string | null;
  }) => string;
  // Helper to get the list title based on UI language with fallback
  getListTitle: (list: {
    title_de?: string | null;
    title_en?: string | null;
    title_tr?: string | null;
    title?: string | null;
  }) => string | null;
}

export const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  // Always use Turkish as the fixed language
  const [language] = useState<UILanguage>('tr');
  const [isLoading, setIsLoading] = useState(true);

  // Language is fixed, no need for auth sync
  useEffect(() => {
    setIsLoading(false);
  }, []);

  // Language is fixed to Turkish, no-op setter for compatibility
  const setLanguage = useCallback(async (_lang: UILanguage) => {
    // No-op: language is fixed to Turkish
  }, []);

  // Helper function to get the Turkish translation (target word)
  const getTargetWord = useCallback((item: {
    target_word_tr?: string | null;
  }): string => {
    return item.target_word_tr?.trim() || '';
  }, []);

  // Helper function to get the appropriate list title based on UI language
  const getListTitle = useCallback((list: {
    title_de?: string | null;
    title_en?: string | null;
    title_tr?: string | null;
    title?: string | null;
  }): string | null => {
    switch (language) {
      case 'en':
        return list.title_en?.trim() || list.title_de?.trim() || list.title?.trim() || null;
      case 'tr':
        return list.title_tr?.trim() || list.title_de?.trim() || list.title?.trim() || null;
      case 'de':
      default:
        return list.title_de?.trim() || list.title?.trim() || null;
    }
  }, [language]);

  const t = translations[language];

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, isLoading, getTargetWord, getListTitle }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}